/* eslint-disable no-console */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {TF} from '../../constants/Common';
import {SampleExcel} from '../../constants/SampleExcel';
import {ISelect} from '../../interface/ISelect';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import waterAPI from '../../services/modules/IP/waterAPI';
import waterSourceJSON from '../../services/modules/JSON/waterSource';
import usePlatformStore from '../../store/platformStore/platformStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {shouldBlockReadOnly} from '../../utils/SubscriptionHelper';
import DisplayModal from '../DisplayModal/DisplayModal';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {
  facilityKeyLabel,
  yearKeyLabel
} from '../Forms/Constants/IP/commonKeyLabel';
import {WaterKey, WaterLabel} from '../Forms/Constants/IP/WaterKeyLabel';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import WaterForm from '../Forms/WaterForm';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {FullInfo, makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import sumValue from './Utils/sumValue';
import TableTitleHeader from './Utils/TableTitleHeader';
import TimeFrameDataTooltip from './Utils/TimeFrameDataTooltip';

const WaterTable: React.FC<{selectedYear: string; facilityId: string}> = ({
  selectedYear,
  facilityId
}) => {
  interface DataType {
    key: React.Key;
    _id: string;
    source: number;
    waterWithdrawn: number;
    waterConsumed: number;
    waterCost: number;
    comment: string;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [openDisplayModal, setOpenDisplayModal] = useState(false);
  const [openDisplayModalData, setOpenDisplayModalData] = useState({});
  const [openDisplayModalType, setOpenDisplayModalType] = useState({
    title: '',
    dataKey: ''
  });
  const [showModelInfo, setShowModelInfo] = useState(false);
  const waterSource = useJSONStore.getState().waterSource;

  useEffect(() => {
    if (!facilityId || !selectedYear) {
      setDataSource([]);
    }
    if (facilityId && selectedYear && allJSON) {
      setIsRefresh(true);
      waterAPI(APITypeEnum.GET_ALL, undefined, `${facilityId}/${selectedYear}`)
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [facilityId, selectedYear, allJSON]);

  useEffect(() => {
    Promise.all([waterSourceJSON()])
      .catch(() => {})
      .finally(() => {
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      waterAPI(APITypeEnum.GET_ALL, undefined, `${facilityId}/${selectedYear}`)
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const handleDelete = (_id: string[] | string) => {
    waterAPI(APITypeEnum.DELETE_ALL, {
      water: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const waterKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: facilityKeyLabel.key,
      label: facilityKeyLabel.label,
      type: objTypes.facilityFormPreview,
      relatedKey: yearKeyLabel.key,
      isMaker: hasUserRole(UserRole.Maker_Role)
    },
    {
      key: WaterKey.WATER_SOURCE,
      label: WaterLabel.WATER_SOURCE,
      type: objTypes.json,
      format: waterSource
    },
    {
      key: TF,
      label: [
        WaterLabel.WATER_WITHDRAWN_TF,
        WaterLabel.WATER_CONSUMED_TF,
        WaterLabel.WATER_COST_TF
      ],
      type: objTypes.timeFrameData,
      renderType: 'multi'
    },
    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = useMemo(() => {
    return [
      {
        title: WaterLabel.WATER_SOURCE,
        dataIndex: WaterKey.WATER_SOURCE,
        editable: true,
        type: 'select',
        ...SearchFilter(
          WaterLabel.WATER_SOURCE,
          WaterKey.WATER_SOURCE,
          waterSource
        ),
        render: (value: any) => {
          return (
            <>
              {
                waterSource.find((item) => {
                  return item.value === value;
                })?.label
              }
            </>
          );
        }
      },

      {
        title: WaterLabel.WATER_WITHDRAWN_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        // render: (value) => {
        //   return (
        //     <TimeFrameRender
        //       value={value}
        //       dataKey={WaterKey.WATER_WITHDRAWN_TF}
        //     />
        //   );
        // }
        render: (value: any) => {
          const mainData = value?.find((item: any) => {
            return item?.type === WaterKey.WATER_WITHDRAWN_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${WaterLabel.WATER_WITHDRAWN_TF} (${
                    mainData?.unit ?? ''
                  })`,
                  dataKey: WaterKey.WATER_WITHDRAWN_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${String(formatIndian(sumValue(mainData?.data)))} ${
                mainData?.unit ?? ''
              }`}</div>
            </div>
          );
        }
      },
      {
        title: WaterLabel.WATER_CONSUMED_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        // render: (value) => {
        //   return (
        //     <TimeFrameRender value={value} dataKey={WaterKey.WATER_CONSUMED_TF} />
        //   );
        // }
        render: (value: any) => {
          const mainData = value?.find((item: any) => {
            return item?.type === WaterKey.WATER_CONSUMED_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${WaterLabel.WATER_CONSUMED_TF} (${
                    mainData?.unit ?? ''
                  })`,
                  dataKey: WaterKey.WATER_CONSUMED_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${String(formatIndian(sumValue(mainData?.data)))} ${
                mainData?.unit ?? ''
              }`}</div>
            </div>
          );
        }
      },
      {
        title: WaterLabel.WATER_COST_TF,
        dataIndex: TF,
        editable: true,
        // ...sorter(WaterKey.WATER_COST_TF as keyof DataType),
        //   width: '100px',
        type: 'select',
        // render: (value) => {
        //   return (
        //     <TimeFrameRender value={value} dataKey={WaterKey.WATER_COST_TF} />
        //   );
        // }
        render: (value: any) => {
          const mainData = value?.find((item: any) => {
            return item?.type === WaterKey.WATER_COST_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${WaterLabel.WATER_COST_TF} (${
                    mainData?.unit ?? ''
                  })`,
                  dataKey: WaterKey.WATER_COST_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${mainData?.unit ?? ''} ${String(
                formatIndian(sumValue(mainData?.data))
              )}`}</div>
            </div>
          );
        }
      },
      ...(hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)
        ? [
            {
              title: makeCheckKeyLabel.STATUS_COMMENTS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <div
                    style={{
                      fontWeight: 700,
                      color: colorText.color,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}{' '}
                    {value === 'rejected' && record?.comment && (
                      <span className="customComment"> - {record.comment}</span>
                    )}
                  </div>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: makeCheckKeyLabel.STATUS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <Tag
                    onClick={() => {
                      if (shouldBlockReadOnly()) {
                        usePlatformStore
                          .getState()
                          .setOpenOnFreeTrialMessageModel(true);

                        return;
                      }
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    color={colorText.color}
                    style={{
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}
                  </Tag>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: FullInfo.title,
              dataIndex: 'FullInfo',
              editable: true,
              type: 'select',
              render: (_: any, record: any) => {
                return (
                  <Button
                    onClick={() => {
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    shape="circle"
                    style={{
                      marginRight: 16,
                      cursor: 'pointer',
                      color: '#435B37',
                      background: '#F5FBF3',
                      border: 0
                    }}
                  >
                    <ExclamationOutlined />
                  </Button>
                );
              }
            }
          ]
        : []),

      ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: Title.actionTitle,
              dataIndex: 'operation',
              type: '' as const,
              fixed: 'right' as const,
              width: '200px',
              render: (value: any, record: any) =>
                dataSource.length >= 1 ? (
                  <ActionsButton
                    record={record}
                    setShowModel={setShowModel}
                    setEditData={setEditData}
                    setIsEdit={setIsEdit}
                    handleDelete={handleDelete}
                  />
                ) : null
            }
          ]
        : [])
    ];
  }, [dataSource]);

  return (
    <>
      <div>
        <TableTitleHeader
          title="Water Info"
          filter={{facilityId, selectedYear}}
          addButton={{
            setShowModel,
            setIsEdit,
            buttonTitle: 'Add Water Consumption'
          }}
          sampleDownload={{href: SampleExcel.water}}
          jobs={{setShowJobs, uploadType: uploadTypes.Water}}
          deleteButton={{selectedRowKeys, handleDelete}}
          refresh={{setRefresh}}
          shouldEdit={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
          }
        />
        <JobsModal
          open={showJobs}
          onCancel={() => {
            setShowJobs(false);
            setRefresh(true);
          }}
          type={uploadTypes.Water}
          details={`${facilityId}/${selectedYear}`}
        />
        <TableAnt
          // getPopupContainer={(triggerNode) =>
          //   triggerNode.closest(
          //     '.ant-modal, .ant-drawer, [style*="position"]'
          //   ) as HTMLElement
          // }
          rowSelection={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
              ? {
                  type: 'checkbox',
                  selectedRowKeys,
                  getCheckboxProps: (record: any) => ({
                    disabled: record.status === statusEm.ACCEPTED
                  }),
                  onChange: (
                    selectedKey: React.Key[],
                    selectedRow: DataType[]
                  ) => {
                    setSelectedRowKeys(
                      selectedRow.map((item: any) => {
                        return item?.status !== statusEm.ACCEPTED
                          ? item._id
                          : null;
                      })
                    );
                  }
                }
              : undefined
          }
          rowKey="_id"
          dataSource={dataSource}
          columns={columns as any}
          scroll={{x: 'auto'}}
          loading={{
            indicator: <Loader />,
            spinning: allJSON ? isRefresh : true
          }}
        />
        <DrawerForm
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          setRefresh={setRefresh}
          data={isEdit ? editData : undefined}
          FormComponent={<WaterForm />}
          API={waterAPI}
          idKey={`${facilityId}/${selectedYear}`}
          table={true}
          stepsTitle={[
            'Configure Details',
            'Water Details',
            'Quantity & Cost Details'
          ]}
          formPreview={waterKeyLabel}
        />
        <DisplayModal
          title={openDisplayModalType.title}
          open={openDisplayModal}
          onCancel={() => {
            setOpenDisplayModal(false);
          }}
          displayComponent={
            <TimeFrameDataTooltip mainData={openDisplayModalData} />
          }
        />
      </div>
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={waterKeyLabel} />
        }
        API={waterAPI}
        isChecker={true}
        isAction={false}
        stepsTitle={['Status & Comments']}
        // formPreview={waterKeyLabel}
      />
    </>
  );
};

export default WaterTable;
